/**
 * What an MCP call answered (components/mcp/McpPanel): the outcome (OK, the tool's `isError`, or the server's JSON-RPC
 * error), the time; then Content (each block: text, an image, audio, an embedded resource, a resource link; or a
 * resource's contents, or a prompt's messages), Structured (`structuredContent`, next to the tool's output schema),
 * Raw (the whole result as JSON), Logs (the service's output while it ran) and HTTP (each hop, with its status).
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { describeBlock, pretty } from '../../utils/mcpTesterModel';

function Block({ block }) {
  const shown = describeBlock(block);
  if (shown.kind === 'text') { return <pre className="data-mono mcp-text">{pretty(shown.text)}</pre>; }
  if (shown.kind === 'image') { return <img alt={`Image (${shown.mimeType}) from the tool`} className="mcp-image" src={shown.src} />; }
  if (shown.kind === 'audio') {
    return <audio aria-label={`Audio (${shown.mimeType}) from the tool`} className="mcp-audio" controls src={shown.src} />;
  }
  if (shown.kind === 'resource') {
    return (
      <div className="mcp-resource">
        <div className="mcp-block-head"><span className="data-mono">{shown.uri}</span>{shown.mimeType && <span className="data-muted">{shown.mimeType}</span>}</div>
        {shown.text !== null ? <pre className="data-mono mcp-text">{pretty(shown.text)}</pre> : <p className="data-muted">Binary ({shown.blobSize ?? 0} bytes)</p>}
      </div>
    );
  }
  if (shown.kind === 'link') {
    return (
      <div className="mcp-resource">
        <div className="mcp-block-head"><span className="mcp-tag">link</span><strong>{shown.name}</strong><span className="data-mono data-muted">{shown.uri}</span></div>
        {shown.description && <p className="data-muted">{shown.description}</p>}
      </div>
    );
  }
  return <pre className="data-mono mcp-text">{shown.json}</pre>;
}

Block.propTypes = { block: PropTypes.any };

function Content({ result }) {
  if (Array.isArray(result.content)) {
    if (!result.content.length) { return <p className="data-muted">No content blocks.</p>; }
    return result.content.map((block, index) => (
      <div className="mcp-block" data-testid="mcp-block" key={index}>
        <span className="mcp-block-type">{block?.type || 'block'}</span>
        <Block block={block} />
      </div>
    ));
  }
  if (Array.isArray(result.contents)) {
    return result.contents.map((item, index) => <div className="mcp-block" data-testid="mcp-block" key={index}><Block block={{ type: 'resource', resource: item }} /></div>);
  }
  if (Array.isArray(result.messages)) {
    return result.messages.map((message, index) => (
      <div className="mcp-block" data-testid="mcp-block" key={index}>
        <span className="mcp-block-type">{message.role}</span>
        <Block block={message.content} />
      </div>
    ));
  }
  return <pre className="data-mono mcp-text">{pretty(result)}</pre>;
}

Content.propTypes = { result: PropTypes.object.isRequired };

export default function McpResult({ answer, error = null, calling = false, outputSchema = null }) {
  const [part, setPart] = useState('content');
  if (calling) { return <div className="api-card mcp-result"><p className="data-muted mcp-pad">Calling…</p></div>; }
  if (error) { return <div className="api-card mcp-result"><p className="data-error-text mcp-pad" role="alert">{error}</p></div>; }
  if (!answer) { return <div className="api-card mcp-result"><p className="data-muted mcp-pad">The result shows here.</p></div>; }
  const result = answer.result || null;
  const structured = result && result.structuredContent !== undefined;
  const parts = [
    ['content', 'Content'],
    ...(structured || outputSchema ? [['structured', 'Structured']] : []),
    ['raw', 'Raw'],
    ['logs', `Logs${answer.logs?.length ? ` ${answer.logs.length}` : ''}`],
    ['http', 'HTTP'],
  ];
  const shownPart = parts.some(([id]) => id === part) ? part : 'content';
  let outcome;
  if (answer.error) { outcome = <span className="data-pill is-bad">Error {answer.error.code}</span>; } else if (result?.isError) { outcome = <span className="data-pill is-bad">isError</span>; } else { outcome = <span className="data-pill is-ok">OK</span>; }
  return (
    <div className="api-card mcp-result" data-testid="mcp-result">
      <div className="mcp-result-head">
        {outcome}
        <span className="data-mono">{answer.method}</span>
        <span className="data-muted">{answer.ms} ms · {answer.transport === 'sse' ? 'SSE' : 'Streamable HTTP'}</span>
        {answer.truncated && <span className="data-pill is-warn" title="Long strings were cut">cut</span>}
      </div>
      <div className="api-sub" role="tablist">
        {parts.map(([id, label]) => (
          <button aria-selected={shownPart === id} className={shownPart === id ? 'is-on' : ''} key={id} onClick={() => setPart(id)} role="tab" type="button">{label}</button>
        ))}
      </div>
      <div className="api-part" role="tabpanel">
        {shownPart === 'content' && answer.error && (
          <div className="mcp-rpc-error" data-testid="mcp-rpc-error">
            <strong>{answer.error.message}</strong>
            {answer.error.data !== undefined && <pre className="data-mono mcp-text">{pretty(answer.error.data)}</pre>}
          </div>
        )}
        {shownPart === 'content' && result && <Content result={result} />}
        {shownPart === 'structured' && (
          <>
            {structured ? <pre className="data-mono mcp-text" data-testid="mcp-structured">{pretty(result.structuredContent)}</pre> : <p className="data-muted">No structuredContent in this result, though the tool declares an output schema.</p>}
            {outputSchema && (
              <details className="mcp-schema">
                <summary>Output schema</summary>
                <pre className="data-mono mcp-text">{pretty(outputSchema)}</pre>
              </details>
            )}
          </>
        )}
        {shownPart === 'raw' && <pre className="data-mono mcp-text">{pretty(answer.error ? { error: answer.error } : result)}</pre>}
        {shownPart === 'logs' && (answer.logs?.length
          ? <pre className="data-mono mcp-text">{answer.logs.map((l) => `${l.type === 'stderr' ? '! ' : ''}${l.line}`).join('\n')}</pre>
          : <p className="data-muted">The service printed nothing while it ran.</p>)}
        {shownPart === 'http' && (
          <table className="mcp-http">
            <tbody>
              {(answer.exchanges || []).map((hop, index) => (
                <tr key={index}><td className="data-mono">{hop.method}</td><td className="data-mono">{hop.path}</td><td>{hop.status}</td><td className="data-muted">{hop.ms} ms</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

McpResult.propTypes = {
  answer: PropTypes.object,
  calling: PropTypes.bool,
  error: PropTypes.string,
  outputSchema: PropTypes.object,
};
